import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UtensilsCrossed, Plus, Radio } from "lucide-react";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function defaultInvoiceRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export default function Mess() {
  const qc = useQueryClient();
  const [planForm, setPlanForm] = useState({
    name: "", description: "", meals_per_day: 1, price_period: "monthly", price: "", meal_value: "",
  });
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [subForm, setSubForm] = useState({ plan_id: "", customer_name: "", customer_phone: "" });
  const [serveForm, setServeForm] = useState({ customer_phone: "", meal_slot: "lunch" });
  const [motdDate, setMotdDate] = useState(today());
  const [motdNotes, setMotdNotes] = useState("");
  const [motdItems, setMotdItems] = useState("");
  const [bulkForm, setBulkForm] = useState({ name: "", type: "hostel", contact_name: "", contact_phone: "", billing_email: "" });
  const [memberForm, setMemberForm] = useState({ account_id: "", plan_id: "", members: "" });
  const [invoice, setInvoice] = useState(null);
  const [invRange, setInvRange] = useState(defaultInvoiceRange);

  const { data: plans = [] } = useQuery({
    queryKey: ["mess-plans"],
    queryFn: async () => (await api.get("/mess/plans")).data,
  });
  const { data: subscriptions = [] } = useQuery({
    queryKey: ["mess-subscriptions"],
    queryFn: async () => (await api.get("/mess/subscriptions")).data,
  });
  const { data: serves = [] } = useQuery({
    queryKey: ["mess-serves", today()],
    queryFn: async () => (await api.get(`/mess/serves?date=${today()}`)).data,
    refetchInterval: 15000,
  });
  const { data: motd } = useQuery({
    queryKey: ["mess-motd", motdDate],
    queryFn: async () => (await api.get(`/mess/menu-of-day?date=${motdDate}`)).data,
  });
  const { data: bulkAccounts = [] } = useQuery({
    queryKey: ["mess-bulk"],
    queryFn: async () => (await api.get("/mess/bulk-accounts")).data,
  });

  useEffect(() => {
    if (!motd) return;
    setMotdNotes(motd.notes || "");
    setMotdItems((motd.items || []).map((i) => i.name).join("\n"));
  }, [motd]);

  const savePlan = async () => {
    if (!planForm.name.trim()) return toast.error("Plan name required");
    try {
      const body = {
        ...planForm,
        meals_per_day: Number(planForm.meals_per_day) || 1,
        price: Number(planForm.price) || 0,
        meal_value: Number(planForm.meal_value) || 0,
      };
      if (editingPlanId) {
        await api.put(`/mess/plans/${editingPlanId}`, body);
        toast.success("Plan updated");
      } else {
        await api.post("/mess/plans", body);
        toast.success("Plan created");
      }
      setEditingPlanId(null);
      setPlanForm({ name: "", description: "", meals_per_day: 1, price_period: "monthly", price: "", meal_value: "" });
      qc.invalidateQueries({ queryKey: ["mess-plans"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createSub = async () => {
    if (!subForm.plan_id || !subForm.customer_phone) return toast.error("Plan and phone required");
    try {
      await api.post("/mess/subscriptions", subForm);
      toast.success("Subscription added");
      setSubForm({ plan_id: "", customer_name: "", customer_phone: "" });
      qc.invalidateQueries({ queryKey: ["mess-subscriptions"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markServe = async () => {
    if (!serveForm.customer_phone) return toast.error("Phone required");
    try {
      const { data } = await api.post("/mess/serve", serveForm);
      toast.success(`Served ${data.customer_name} · wallet ₹${data.wallet_balance_after}`);
      setServeForm({ ...serveForm, customer_phone: "" });
      qc.invalidateQueries({ queryKey: ["mess-serves"] });
      qc.invalidateQueries({ queryKey: ["mess-subscriptions"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveMotd = async () => {
    const items = motdItems
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
    try {
      await api.put("/mess/menu-of-day", { date: motdDate, items, notes: motdNotes });
      toast.success("Menu of day saved");
      qc.invalidateQueries({ queryKey: ["mess-motd"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const broadcastMotd = async () => {
    try {
      const { data } = await api.post("/mess/menu-of-day/broadcast", { date: motdDate, channel: "whatsapp" });
      toast.success(`Broadcast to ${data.sent} of ${data.total}`);
      qc.invalidateQueries({ queryKey: ["mess-motd"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const createBulk = async () => {
    if (!bulkForm.name.trim()) return toast.error("Account name required");
    try {
      await api.post("/mess/bulk-accounts", bulkForm);
      toast.success("Bulk account created");
      setBulkForm({ name: "", type: "hostel", contact_name: "", contact_phone: "", billing_email: "" });
      qc.invalidateQueries({ queryKey: ["mess-bulk"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addMembers = async () => {
    if (!memberForm.account_id || !memberForm.plan_id) return toast.error("Account and plan required");
    const members = memberForm.members
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [name, phone] = line.includes(",") ? line.split(",").map((s) => s.trim()) : ["Member", line];
        return { name: name || "Member", phone: phone || name };
      })
      .filter((m) => m.phone);
    if (!members.length) return toast.error("Add members as name,phone per line");
    try {
      const { data } = await api.post(`/mess/bulk-accounts/${memberForm.account_id}/members`, {
        plan_id: memberForm.plan_id,
        members,
      });
      toast.success(`Added ${data.created} members`);
      setMemberForm({ ...memberForm, members: "" });
      qc.invalidateQueries({ queryKey: ["mess-bulk"] });
      qc.invalidateQueries({ queryKey: ["mess-subscriptions"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const loadInvoice = async (accountId) => {
    try {
      const { data } = await api.get(
        `/mess/bulk-accounts/${accountId}/invoice?from=${invRange.from}&to=${invRange.to}`,
      );
      setInvoice(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="mess-page">
      <PageHeader title="Mess & Canteen" subtitle="Meal plans, attendance, menu of day, and bulk billing" />

      <Tabs defaultValue="plans" className="max-w-5xl">
        <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="serve">Serve</TabsTrigger>
          <TabsTrigger value="motd">Menu of Day</TabsTrigger>
          <TabsTrigger value="bulk">Bulk accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="space-y-4">
          <div className="kul-card p-5 space-y-3">
            <h3 className="font-display font-700 flex items-center gap-2"><Plus className="w-4 h-4" /> New plan</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} /></div>
              <div>
                <Label>Period</Label>
                <select value={planForm.price_period} onChange={(e) => setPlanForm({ ...planForm, price_period: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div><Label>Meals / day</Label><Input type="number" value={planForm.meals_per_day} onChange={(e) => setPlanForm({ ...planForm, meals_per_day: e.target.value })} /></div>
              <div><Label>Price</Label><Input type="number" value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })} /></div>
              <div className="sm:col-span-2"><Label>Description</Label><Input value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} /></div>
            </div>
            <Button onClick={savePlan} className="bg-[var(--kul-primary)] text-white">{editingPlanId ? "Update plan" : "Create plan"}</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {plans.map((p) => (
              <div key={p.id} className="kul-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold">{p.name}</div>
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingPlanId(p.id);
                    setPlanForm({
                      name: p.name || "",
                      description: p.description || "",
                      meals_per_day: p.meals_per_day || 1,
                      price_period: p.price_period || "monthly",
                      price: p.price ?? "",
                      meal_value: p.meal_value ?? "",
                    });
                  }}>Edit</Button>
                </div>
                <div className="text-sm text-neutral-500 mt-1">{p.description || "—"}</div>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <Badge className="bg-neutral-100 text-neutral-600 capitalize">{p.price_period}</Badge>
                  <Badge className="bg-orange-50 text-[var(--kul-primary)]">{money(p.price)}</Badge>
                  <span className="text-neutral-400">{p.meals_per_day} meal/day · ₹{p.meal_value}/serve</span>
                </div>
              </div>
            ))}
            {!plans.length && <p className="text-neutral-400">No plans yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="space-y-4">
          <div className="kul-card p-5 space-y-3">
            <h3 className="font-display font-700">Enroll member</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Plan</Label>
                <select value={subForm.plan_id} onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  <option value="">Select…</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div><Label>Name</Label><Input value={subForm.customer_name} onChange={(e) => setSubForm({ ...subForm, customer_name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={subForm.customer_phone} onChange={(e) => setSubForm({ ...subForm, customer_phone: e.target.value })} /></div>
            </div>
            <Button onClick={createSub} className="bg-[var(--kul-primary)] text-white">Subscribe</Button>
          </div>
          <div className="kul-card p-5">
            <h3 className="font-display font-700 mb-3">Active subscriptions</h3>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {subscriptions.map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b pb-2 text-sm gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.customer_name} · {s.customer_phone}</div>
                    <div className="text-xs text-neutral-400">{s.plan_name} · {s.starts_at} → {s.ends_at || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge className="capitalize mb-1">{s.status}</Badge>
                    <div className="font-mono-k text-xs">₹{Number(s.wallet_balance || 0).toFixed(0)}</div>
                  </div>
                </div>
              ))}
              {!subscriptions.length && <p className="text-neutral-400">No subscriptions.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="serve" className="space-y-4">
          <div className="kul-card p-5 space-y-3 max-w-lg">
            <h3 className="font-display font-700 flex items-center gap-2"><UtensilsCrossed className="w-4 h-4" /> Mark attendance</h3>
            <div><Label>Phone</Label><Input value={serveForm.customer_phone} onChange={(e) => setServeForm({ ...serveForm, customer_phone: e.target.value })} placeholder="Member phone" /></div>
            <div>
              <Label>Meal slot</Label>
              <select value={serveForm.meal_slot} onChange={(e) => setServeForm({ ...serveForm, meal_slot: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <Button onClick={markServe} className="bg-[var(--kul-primary)] text-white">Serve meal</Button>
          </div>
          <div className="kul-card p-5">
            <h3 className="font-display font-700 mb-3">Today&apos;s serves</h3>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {serves.map((s) => (
                <div key={s.id} className="text-sm flex justify-between border-b pb-2">
                  <div>
                    <span className="font-medium">{s.customer_name}</span>
                    <span className="text-neutral-400 ml-2 capitalize">{s.meal_slot}</span>
                  </div>
                  <span className="text-xs text-neutral-400">{fmtTime(s.created_at)} · ₹{s.amount}</span>
                </div>
              ))}
              {!serves.length && <p className="text-neutral-400">No serves yet today.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="motd" className="space-y-4">
          <div className="kul-card p-5 space-y-3">
            <h3 className="font-display font-700">Menu of the day</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={motdDate} onChange={(e) => setMotdDate(e.target.value)} /></div>
              <div><Label>Notes</Label><Input value={motdNotes} onChange={(e) => setMotdNotes(e.target.value)} placeholder="Extra roti on request" /></div>
            </div>
            <div>
              <Label>Items (one per line)</Label>
              <textarea
                rows={5}
                className="w-full rounded-lg border p-2 text-sm"
                value={motdItems}
                onChange={(e) => setMotdItems(e.target.value)}
                placeholder={"Dal fry\nJeera rice\nSalad"}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveMotd} className="bg-[var(--kul-primary)] text-white">Save menu</Button>
              <Button variant="outline" onClick={broadcastMotd}><Radio className="w-4 h-4 mr-1" /> Broadcast WhatsApp</Button>
            </div>
            {motd?.broadcast_at && (
              <p className="text-xs text-neutral-400">Last broadcast {new Date(motd.broadcast_at).toLocaleString("en-IN")}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bulk" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="kul-card p-5 space-y-3">
              <h3 className="font-display font-700">New bulk account</h3>
              <div><Label>Name</Label><Input value={bulkForm.name} onChange={(e) => setBulkForm({ ...bulkForm, name: e.target.value })} placeholder="Hostel Block A" /></div>
              <div>
                <Label>Type</Label>
                <select value={bulkForm.type} onChange={(e) => setBulkForm({ ...bulkForm, type: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  <option value="hostel">Hostel</option>
                  <option value="corporate">Corporate</option>
                  <option value="family">Family</option>
                </select>
              </div>
              <div><Label>Contact</Label><Input value={bulkForm.contact_name} onChange={(e) => setBulkForm({ ...bulkForm, contact_name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={bulkForm.contact_phone} onChange={(e) => setBulkForm({ ...bulkForm, contact_phone: e.target.value })} /></div>
              <div><Label>Billing email</Label><Input value={bulkForm.billing_email} onChange={(e) => setBulkForm({ ...bulkForm, billing_email: e.target.value })} /></div>
              <Button onClick={createBulk} className="bg-[var(--kul-primary)] text-white">Create</Button>
            </div>

            <div className="kul-card p-5 space-y-3">
              <h3 className="font-display font-700">Add members</h3>
              <div>
                <Label>Account</Label>
                <select value={memberForm.account_id} onChange={(e) => setMemberForm({ ...memberForm, account_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  <option value="">Select…</option>
                  {bulkAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.member_count || 0})</option>)}
                </select>
              </div>
              <div>
                <Label>Plan</Label>
                <select value={memberForm.plan_id} onChange={(e) => setMemberForm({ ...memberForm, plan_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm">
                  <option value="">Select…</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Members (name,phone per line)</Label>
                <textarea rows={4} className="w-full rounded-lg border p-2 text-sm" value={memberForm.members} onChange={(e) => setMemberForm({ ...memberForm, members: e.target.value })} />
              </div>
              <Button onClick={addMembers} variant="outline">Add members</Button>
            </div>
          </div>

          <div className="kul-card p-5 space-y-3">
            <h3 className="font-display font-700">Invoice</h3>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={invRange.from} onChange={(e) => setInvRange({ ...invRange, from: e.target.value })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={invRange.to} onChange={(e) => setInvRange({ ...invRange, to: e.target.value })} className="h-9" />
              </div>
              <select
                className="h-9 rounded-lg border px-2 text-sm"
                defaultValue=""
                onChange={(e) => { if (e.target.value) loadInvoice(e.target.value); }}
              >
                <option value="">Load invoice for…</option>
                {bulkAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            {invoice && (
              <div className="rounded-xl bg-neutral-50 p-4 text-sm space-y-1">
                <div className="font-semibold">{invoice.account?.name}</div>
                <div>{invoice.members} members · {invoice.serves} serves</div>
                <div className="font-display font-700 text-lg text-[var(--kul-primary)]">{money(invoice.total)}</div>
                <div className="text-xs text-neutral-400">{invoice.from} → {invoice.to}</div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
